import React from "react";

import type { Theme } from "@mui/material";
import Chip, { ChipProps } from "@mui/material/Chip";
import styled from "@mui/material/styles/styled";

import { pluralizePhrase } from "@app/common/utils";

const getChipBorderColor = (
  theme: Theme,
  daysRemaining: number
): React.CSSProperties["borderColor"] => {
  if (daysRemaining >= 5) return theme.palette.shades.primary[400];
  if (daysRemaining >= 3) return theme.palette.shades.orange[500];
  return theme.palette.shades.negative[500];
};

type TrialChipProps = Omit<ChipProps, "color" | "backgroundColor"> & {
  backgroundColor: React.CSSProperties["backgroundColor"];
  color?: React.CSSProperties["color"];
  daysRemaining: number;
};

const TrialChip = styled<React.FC<TrialChipProps>>(
  ({ backgroundColor: _bc, color: _c, daysRemaining: _dr, ...props }) => (
    <Chip {...props} />
  )
)(({ backgroundColor, color, daysRemaining, theme }) => {
  const borderColor = getChipBorderColor(theme, daysRemaining);
  return {
    position: "relative",
    padding: 0,
    width: "fit-content",
    height: "auto",
    margin: "1px", // offset for the ::before
    "&::before": {
      content: "''",
      borderRadius: theme.corners.base,
      backgroundImage: theme.palette.gradients.data,
      position: "absolute",
      top: -1,
      bottom: -1,
      left: -1,
      right: -1,
      zIndex: 0,
    },
    backgroundColor: "black",
    "& .MuiChip-label": {
      height: "100%",
      width: "100%",
      color: color ?? borderColor,
      fontSize: "0.625rem",
      fontWeight: 500,
      textOverflow: "none",
      overflow: "visible",
      padding: "2px 4px",
      zIndex: 1,
      borderRadius: theme.corners.base,
      backgroundColor,
    },
  };
});

const TrialCountdownChip = ({
  backgroundColor = "#FFF",
  daysRemaining,
  expanded,
  textColor,
}: {
  daysRemaining: number;
  expanded?: boolean;
  backgroundColor?: React.CSSProperties["backgroundColor"];
  textColor?: React.CSSProperties["color"];
}) => {
  if (daysRemaining <= 0) return null;

  return (
    <TrialChip
      daysRemaining={daysRemaining}
      backgroundColor={backgroundColor}
      color={textColor}
      label={
        expanded
          ? `Free Trial (${pluralizePhrase(daysRemaining, "day")} left)`
          : "Trial"
      }
    />
  );
};

export default TrialCountdownChip;
